import { z } from 'zod';
import { CHECKIN_KINDS, GENDERS, STATION_MODES } from '../enums';
import { isValidSecurityCode } from '../misc';
import { isValidPassCode, normalizePassCode } from '../barcode';
import { zDateOnly, zId, zIdList, zOptId, zOptText, zText, zTime } from './common';

export const checkinEventSchema = z.object({
  campusId: zId,
  name: zText(80),
  frequency: z.enum(['weekly', 'none']).default('weekly'),
  archived: z.boolean().default(false),
  /** Las familias pueden dejar lista su selección desde el teléfono. */
  allowPrecheck: z.boolean().default(false),
});

export const eventTimeSchema = z
  .object({
    weekday: z.number().int().min(0).max(6),
    startTime: zTime,
    endTime: zTime,
    opensMinutesBefore: z.number().int().min(0).max(240).default(30),
  })
  .refine((t) => t.endTime > t.startTime, { message: 'La hora de fin debe ser posterior', path: ['endTime'] });

export const openSessionsSchema = z.object({ eventId: zId, date: zDateOnly });

export const locationSchema = z
  .object({
    eventId: zId,
    name: zText(80),
    minAgeMonths: z.number().int().min(0).max(1200).nullable().optional(),
    maxAgeMonths: z.number().int().min(0).max(1200).nullable().optional(),
    minGrade: z.number().int().min(-1).max(12).nullable().optional(),
    maxGrade: z.number().int().min(-1).max(12).nullable().optional(),
    gender: z.enum(GENDERS).nullable().optional(),
    capacity: z.number().int().min(1).max(1000).nullable().optional(),
    childRatio: z.number().int().min(1).max(50).nullable().optional(),
    forVolunteers: z.boolean().default(false),
    sortOrder: z.number().int().min(0).max(999).default(0),
  })
  .refine((l) => l.minAgeMonths == null || l.maxAgeMonths == null || l.maxAgeMonths >= l.minAgeMonths, {
    message: 'La edad máxima debe ser mayor o igual a la mínima',
    path: ['maxAgeMonths'],
  })
  .refine((l) => l.minGrade == null || l.maxGrade == null || l.maxGrade >= l.minGrade, {
    message: 'El grado máximo debe ser mayor o igual al mínimo',
    path: ['maxGrade'],
  });

/** Dirección de una impresora en red: «192.168.1.50» o «192.168.1.50:9100». */
export const PRINTER_ADDRESS_RE = /^[A-Za-z0-9.-]+(:\d{1,5})?$/;

export const printerSchema = z
  .object({
    campusId: zId,
    name: zText(80),
    /** browser = impresión del navegador (cualquier impresora); zpl = Zebra en red vía agente local. */
    kind: z.enum(['browser', 'zpl']).default('browser'),
    address: zOptText(200),
    labelSize: z.enum(['4x2', '2.25x1.25']).default('4x2'),
  })
  .refine((p) => p.kind !== 'zpl' || (!!p.address && PRINTER_ADDRESS_RE.test(p.address)), {
    message: 'Indica la dirección IP de la impresora (por ejemplo 192.168.1.50:9100)',
    path: ['address'],
  });

export const stationUpdateSchema = z.object({
  name: zText(80).optional(),
  mode: z.enum(STATION_MODES).optional(),
  printerId: zOptId,
  eventIds: zIdList.optional(),
});

export const stationSchema = z.object({
  campusId: zId,
  name: zText(80),
  mode: z.enum(STATION_MODES).default('self'),
  printerId: zOptId,
  eventIds: zIdList.default([]),
});

export const kioskSearchSchema = z.object({ stationId: zId, query: z.string().trim().min(2).max(60) });

const checkinEntries = z
  .array(
    z.object({
      personId: zId,
      sessionId: zId,
      locationId: zId,
      kind: z.enum(CHECKIN_KINDS).default('regular'),
    }),
  )
  .min(1)
  .max(30);

export const checkinCreateSchema = z.object({
  stationId: zId,
  entries: checkinEntries,
  /** Registros previos del teléfono que se completan con este registro. */
  precheckIds: z.array(zId).max(5).optional(),
});

/** Registro previo desde el teléfono: la selección de la familia, que se confirma al llegar. */
export const precheckCreateSchema = z.object({ campusId: zId, entries: checkinEntries });

/** Registros que un kiosko hizo sin conexión y envía al volver el internet. */
export const offlineSyncSchema = z.object({
  stationId: zId,
  records: z
    .array(
      z.object({
        clientRef: z.string().uuid(),
        securityCode: z.string().refine(isValidSecurityCode, 'Código de seguridad inválido'),
        checkedInAt: z.string().datetime({ offset: true }),
        entries: checkinEntries,
      }),
    )
    .min(1)
    .max(50),
});

/**
 * Salida: código de la etiqueta del padre + quién recoge. Quien recoge debe ser un adulto
 * del hogar del niño o una persona autorizada (pickedUpByPersonId o authorizedPickupId).
 * Cualquier otra persona (solo pickedUpByName) requiere que un administrador autorice (override).
 */
export const checkoutSchema = z.object({
  securityCode: z.string().trim().toUpperCase().length(4),
  pickedUpByPersonId: zOptId,
  authorizedPickupId: zOptId,
  pickedUpByName: zOptText(120),
  override: z.boolean().default(false),
});
export const checkoutByCodeSchema = checkoutSchema.omit({ securityCode: true }).extend({
  campusId: zId,
  securityCode: z.string().trim().toUpperCase().length(4),
});

export const authorizedPickupSchema = z
  .object({
    childPersonId: zId,
    pickupPersonId: zOptId,
    pickupName: zOptText(120),
    phone: zOptText(30),
    relationship: zOptText(60),
    notes: zOptText(500),
  })
  .refine((p) => p.pickupPersonId || p.pickupName, { message: 'Indica quién recoge', path: ['pickupName'] });

export const headcountSchema = z.object({
  sessionId: zId,
  locationId: zOptId,
  attendanceType: zText(40),
  count: z.number().int().min(0).max(100000),
});

/** Asignar una tarjeta: el código que trae (escaneado) o, sin código, uno nuevo generado. */
export const passSchema = z.object({
  personId: zId,
  code: z
    .string()
    .transform(normalizePassCode)
    .refine((c) => c === '' || isValidPassCode(c), 'Código de tarjeta inválido: usa de 4 a 32 letras, números o guiones')
    .transform((c) => c || null)
    .optional(),
});

/** Crear tarjetas para las familias de un campus con niños que todavía no tienen ninguna. */
export const passBulkSchema = z.object({ campusId: zId, dryRun: z.boolean().default(true) });

export const labelTemplateSchema = z.object({
  name: zText(80),
  kind: z.enum(['child', 'parent', 'name_tag']),
  widthIn: z.number().min(1).max(8),
  heightIn: z.number().min(0.5).max(8),
  showMedical: z.boolean().default(true),
  showCode: z.boolean().default(true),
});
